import clsx from 'clsx';
import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  CalendarOff,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CopyPlus,
  GripVertical,
  Lock,
  Pause,
  Pencil,
  Play,
  Plus,
  Repeat,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { addDays, isWeekend, weekStart } from '@pz/core';
import type { EntryView, TaskView } from '@pz/server';
import { EntryActions, EntryDialog, ProjectDot, TaskBadges, toPicked, type PickedTask, type RecurringRule } from '../components/entry';
import { LeaveDialog } from '../components/leave';
import { CopyWeekDialog, FavoriteStar, useFavorites } from '../components/quick';
import {
  Button,
  Checkbox,
  ConfirmModal,
  ErrorBox,
  Field,
  Gauge,
  IconButton,
  Input,
  Loading,
  Modal,
  Notice,
  PageHeader,
  Pill,
  Select,
  StatusPill,
  useToast,
} from '../components/ui';
import { projectColorVar } from '../lib/colors';
import { d, h, hh, kw, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useMe } from '../lib/me';

/** Saturday and Sunday on demand; the choice is kept in this browser only. */
const WEEKEND_KEY = 'pz.week.showWeekend';
function readWeekendPref() {
  try {
    return localStorage.getItem(WEEKEND_KEY) === '1';
  } catch {
    return false;
  }
}

/** A Monday for the weekday names of recurring entries. */
const WEEK_REFERENCE = '2026-10-05';

type PanelSort = 'manual' | 'hours' | 'project';

/** What is being dragged: an entry of the week, or a task of the side panel (favorite or recently used). */
type Drag = { kind: 'entry'; id: string } | { kind: 'task'; task: TaskView; from: 'fav' | 'recent' } | null;

/** Small picture under the pointer while dragging: task number, title and hours (instead of a copy of the whole card). */
function setDragPicture(ev: DragEvent, text: string) {
  try {
    const el = document.createElement('div');
    el.textContent = text;
    el.style.cssText =
      'position:fixed;top:-1000px;left:-1000px;max-width:260px;padding:6px 10px;border-radius:8px;font:600 12px/1.3 system-ui,sans-serif;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
      'background:var(--accent);color:var(--accent-ink);box-shadow:0 6px 18px rgb(0 0 0 / 0.25)';
    document.body.appendChild(el);
    ev.dataTransfer.setDragImage(el, 14, 14);
    setTimeout(() => el.remove(), 0);
  } catch {
    /* the browser shows its own picture */
  }
}

export function WeekPage() {
  const { t } = useTranslation();
  const { me, can } = useMe();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const ws = weekStart(params.get('w') ?? me.today);
  const personId = params.get('p') ?? undefined;
  const q = useQ('time.week', { weekStart: ws, personId });
  const canViewOthers = can('time.manage', 'time.approve.final', 'kpi.team.view');
  const people = useQ('people.list', {}, { enabled: canViewOthers });
  const own = !personId || personId === me.person.id;
  const recent = useQ('tasks.recent', {}, { enabled: own });
  const favs = useFavorites(own);
  const panelPref = useQ('prefs.get', { keys: ['quick.panel'] }, { enabled: own });
  const setPref = useM('prefs.set', { invalidate: ['prefs.'] });
  const rules = useQ('recurring.list', {}, { enabled: own });
  const submitWeek = useM('time.submitWeek', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const withdraw = useM('time.withdraw', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const [dialog, setDialog] = useState<{ open: boolean; entry?: EntryView | null; date?: string; task?: PickedTask | null; rule?: RecurringRule | null }>({ open: false });
  const [absence, setAbsence] = useState<string | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  // moving and copying single entries: drag to another day (Ctrl or Alt held: copy) or the menu of the entry
  const updateM = useM('time.update', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const copyM = useM('time.copy', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const deleteM = useM('time.delete', { invalidate: ['time.', 'approvals.', 'kpi.'] });
  const orderM = useM('quick.orderFavorites', { invalidate: ['quick.'] });
  const pinM = useM('quick.toggleFavorite', { invalidate: ['quick.'] });
  const applyM = useM('recurring.apply', { invalidate: ['time.', 'kpi.', 'cockpit.'] });
  const saveRuleM = useM('recurring.save', { invalidate: ['recurring.', 'time.'] });
  const deleteRuleM = useM('recurring.delete', { invalidate: ['recurring.'] });
  const [drag, setDrag] = useState<Drag>(null);
  const [dropDay, setDropDay] = useState<string | null>(null);
  const [dropCopy, setDropCopy] = useState(false);
  // insertion point in the favorites while a task is dragged over them (index before which it goes)
  const [favDrop, setFavDrop] = useState<number | null>(null);
  const [moveFor, setMoveFor] = useState<EntryView | null>(null);
  const [moveDate, setMoveDate] = useState('');
  const [ruleToDelete, setRuleToDelete] = useState<RecurringRule | null>(null);
  const moveOrCopy = async (e: EntryView, workDate: string, copy: boolean) => {
    if (workDate === e.workDate && !copy) return;
    try {
      if (copy) {
        const r = await copyM.mutateAsync({ id: e.id, workDate });
        toast.undoable(t('week.copied', { date: d(workDate) }), t('common.undo'), () =>
          deleteM.mutateAsync({ id: r.entry.id }).then(() => toast.ok(t('common.undone'))).catch(toast.error),
        );
      } else {
        const r = await updateM.mutateAsync({ id: e.id, version: e.version, workDate });
        toast.undoable(t('week.moved', { date: d(workDate) }), t('common.undo'), () =>
          updateM
            .mutateAsync({ id: e.id, version: r.entry.version, workDate: e.workDate })
            .then(() => toast.ok(t('common.undone')))
            .catch(toast.error),
        );
      }
    } catch (err) {
      toast.error(err);
    }
  };
  const [weekendPref, setWeekendPref] = useState(readWeekendPref);
  const toggleWeekend = (on: boolean) => {
    setWeekendPref(on);
    try {
      localStorage.setItem(WEEKEND_KEY, on ? '1' : '0');
    } catch {
      /* private mode: keep the choice for this visit only */
    }
  };

  const doSubmit = () =>
    submitWeek
      .mutateAsync({ weekStart: ws, personId })
      .then((r) =>
        r.submitted
          ? toast.undoable(t('week.submitDone', { count: r.submitted }), t('common.undo'), () =>
              withdraw
                .mutateAsync({ ids: r.ids })
                .then((w) => toast.ok(t('entry.withdrawnDone', { count: w.withdrawn })))
                .catch(toast.error),
            )
          : toast.info(t('week.nothingToSubmit')),
      )
      .catch(toast.error);

  const go = (w: string) => {
    const next = new URLSearchParams(params);
    next.set('w', w);
    setParams(next, { replace: true });
  };

  const data = q.data;
  // recurring entries: the days of the shown week are booked as drafts when the week is opened (once per week and visit)
  const applied = useRef(new Set<string>());
  useEffect(() => {
    if (!own || !data?.recurringPending || applied.current.has(ws)) return;
    applied.current.add(ws);
    applyM
      .mutateAsync({ weekStart: ws })
      .then((r) => {
        if (r.created) toast.info(t('recurring.bookedNow', { count: r.created }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [own, data?.recurringPending, ws]);

  const days = data?.days ?? [];
  // normally Monday to Friday; Saturday and Sunday appear on demand and always when booked, when they are
  // working days by contract or when today is a weekend day of the shown week
  const weekendNeeded =
    days.some((x) => isWeekend(x.date) && (x.bookedMinutes > 0 || (x.baseMinutes ?? 0) > 0)) || (isWeekend(me.today) && me.today >= ws && me.today <= addDays(ws, 6));
  const showWeekend = weekendPref || weekendNeeded;
  // target hours of other people are shown only to Personal (HR) and management (null otherwise)
  const showTarget = data?.totals.capacityMinutes !== null && data?.totals.capacityMinutes !== undefined;
  const comp = data?.totals.compensationMinutes ?? 0;
  const visibleDays = days.filter((x) => showWeekend || !isWeekend(x.date));
  // a click on a task of the side panel books today when today is in the shown week, otherwise Monday of that week
  const bookDate = me.today >= ws && me.today <= addDays(ws, 6) ? me.today : ws;

  // side panel: own order, most hours of the last 90 days, or grouped by project
  const sort: PanelSort = (panelPref.data?.['quick.panel']?.sort as PanelSort | undefined) ?? 'manual';
  const setSort = (v: PanelSort) => setPref.mutateAsync({ key: 'quick.panel', value: { sort: v } }).catch(toast.error);
  const usage = favs.usage;
  const sortTasks = (list: TaskView[]) => {
    if (sort === 'hours') return [...list].sort((a, b) => (usage[b.id] ?? 0) - (usage[a.id] ?? 0));
    if (sort === 'project') return [...list].sort((a, b) => a.projectNumber.localeCompare(b.projectNumber, undefined, { numeric: true }));
    return list;
  };
  const favTasks = useMemo(() => sortTasks(favs.tasks), [favs.tasks, sort, usage]); // eslint-disable-line react-hooks/exhaustive-deps
  const recentOnly = useMemo(() => sortTasks((recent.data ?? []).filter((r) => !favs.ids.has(r.id))), [recent.data, favs.ids, sort, usage]); // eslint-disable-line react-hooks/exhaustive-deps

  const dragging = !!drag;
  const endDrag = () => {
    setDrag(null);
    setDropDay(null);
    setFavDrop(null);
  };
  const reorder = (taskId: string, index: number) => {
    const ids = favs.tasks.map((x) => x.id).filter((x) => x !== taskId);
    const at = Math.max(0, Math.min(ids.length, index));
    ids.splice(at, 0, taskId);
    return orderM.mutateAsync({ taskIds: ids }).catch(toast.error);
  };
  const dropOnFavorites = (index: number) => {
    if (!drag || drag.kind !== 'task') return;
    if (drag.from === 'fav') {
      const from = favs.tasks.findIndex((x) => x.id === drag.task.id);
      // the gap left by the task itself does not count
      reorder(drag.task.id, from >= 0 && from < index ? index - 1 : index);
    } else {
      pinM
        .mutateAsync({ taskId: drag.task.id, position: index })
        .then(() => toast.ok(t('quick.pinnedAt', { ref: drag.task.ref })))
        .catch(toast.error);
    }
  };
  const ruleList = rules.data?.rules ?? [];

  return (
    <div>
      <PageHeader
        title={own ? t('week.title') : t('week.titleOther', { name: data?.person.name ?? '' })}
        actions={
          <>
            {canViewOthers && (
              <Select
                aria-label={t('week.viewPerson')}
                className="w-56"
                value={personId ?? ''}
                onChange={(e) => {
                  const next = new URLSearchParams(params);
                  if (e.target.value) next.set('p', e.target.value);
                  else next.delete('p');
                  setParams(next, { replace: true });
                }}
              >
                <option value="">{t('week.me')}</option>
                {(people.data?.people ?? [])
                  .filter((p) => p.id !== me.person.id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}
                    </option>
                  ))}
              </Select>
            )}
            {own && !!data?.withdrawIds.length && (
              <Button
                icon={<Undo2 className="size-4" />}
                loading={withdraw.isPending}
                onClick={() =>
                  withdraw
                    .mutateAsync({ ids: data.withdrawIds })
                    .then((w) => toast.ok(t('entry.withdrawnDone', { count: w.withdrawn })))
                    .catch(toast.error)
                }
              >
                {t('week.withdrawWeek')}
              </Button>
            )}
            {data?.canSubmit && (
              <Button variant="primary" icon={<Send className="size-4" />} loading={submitWeek.isPending} onClick={doSubmit}>
                {t('week.submitWeek')}
              </Button>
            )}
            {data?.canMarkAbsence && (
              <Button icon={<CalendarOff className="size-4" />} onClick={() => setAbsence(me.today >= ws && me.today <= addDays(ws, 6) ? me.today : ws)}>
                {t('week.markAbsence')}
              </Button>
            )}
            {own && (
              <Button icon={<CopyPlus className="size-4" />} onClick={() => setCopyOpen(true)} title={t('quick.copyIntro', { week: kw(ws) })}>
                {t('quick.copyWeek')}
              </Button>
            )}
            <Button icon={<Plus className="size-4" />} onClick={() => setDialog({ open: true, date: me.today >= ws && me.today <= addDays(ws, 6) ? me.today : ws })}>
              {t('week.addEntry')}
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-sheet px-3 py-2">
        <div className="flex items-center gap-1">
          <IconButton label={t('common.previous')} icon={<ChevronLeft className="size-4" />} onClick={() => go(addDays(ws, -7))} />
          <div className="num min-w-44 text-center text-sm">
            <span className="font-semibold">
              {t('common.cw')} {kw(ws)}
            </span>
            <span className="text-graphite">
              {' '}
              {d(ws)} - {d(addDays(ws, 6))}
            </span>
          </div>
          <IconButton label={t('common.next')} icon={<ChevronRight className="size-4" />} onClick={() => go(addDays(ws, 7))} />
          <Button size="sm" variant="ghost" onClick={() => go(weekStart(me.today))}>
            {t('common.today')}
          </Button>
        </div>
        <span title={weekendNeeded ? t('week.weekendNeeded') : t('week.showWeekendHint')}>
          <Checkbox label={t('week.showWeekend')} checked={showWeekend} disabled={weekendNeeded} onChange={(e) => toggleWeekend(e.target.checked)} />
        </span>
        {data && (
          <div className="ml-auto flex min-w-56 flex-1 items-center gap-3 sm:max-w-sm">
            <span className="num text-sm whitespace-nowrap">
              <span className="font-semibold">{h(data.totals.bookedMinutes)}</span>
              {showTarget && <span className="text-graphite"> / {hh(data.totals.capacityMinutes ?? 0)}</span>}
              {showTarget && comp > 0 && <span className="block text-xs text-graphite">{t('week.compHint', { hours: h(comp) })}</span>}
            </span>
            {showTarget && (
              <Gauge value={data.totals.bookedMinutes} capacity={data.totals.capacityMinutes ?? 0} base={days.reduce((s, x) => s + (x.baseMinutes ?? 0), 0)} comp={comp} />
            )}
          </div>
        )}
      </div>

      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {data && days.some((x) => x.locked) && (
        <Notice tone="neutral" className="mb-3">
          <Lock className="mr-1 inline size-3.5" />
          {t('week.lockedHint')}
        </Notice>
      )}
      {/* while dragging: what letting go does (screen readers get the same through the menu "Verschieben oder kopieren") */}
      {dragging && (
        <div className="pointer-events-none sticky top-2 z-30 mb-3 flex justify-center" aria-hidden>
          <span className="rounded-full border border-accent bg-sheet px-3 py-1 text-xs font-medium text-accent shadow-md">
            {drag?.kind === 'entry' ? t('week.dragEntryHint') : t('week.dragTaskHint')}
          </span>
        </div>
      )}

      {data && (
        <div className="flex flex-col gap-4 2xl:flex-row">
          <div className={clsx('grid min-w-0 flex-1 gap-3', showWeekend ? 'md:grid-cols-2 xl:grid-cols-7' : 'md:grid-cols-2 xl:grid-cols-5')}>
            {visibleDays.map((day) => {
              const entries = data.entries.filter((e) => e.workDate === day.date);
              const weekend = isWeekend(day.date);
              const today = day.date === me.today;
              // a weekday without contract hours (e.g. part time); weekends and holidays speak for themselves
              const freeDay = day.baseMinutes === 0 && !weekend && !day.holidayName;
              // the day against its target: complete (green), above (warm), below (neutral); only with a visible target
              const cap = day.capacityMinutes;
              const state = cap === null ? null : day.bookedMinutes > cap && day.bookedMinutes > 0 ? 'over' : cap > 0 && day.bookedMinutes === cap ? 'full' : null;
              const target = dragging && !day.locked && (drag?.kind === 'task' || (drag?.kind === 'entry' && own));
              const hovered = dropDay === day.date && target;
              return (
                <section
                  key={day.date}
                  aria-label={`${wd(day.date)} ${d(day.date)}`}
                  className={clsx(
                    'relative flex min-w-0 flex-col rounded-lg border bg-sheet transition-[box-shadow,opacity]',
                    today ? 'border-accent' : 'border-line',
                    weekend && 'bg-sheet-2',
                    target && !hovered && 'outline-2 -outline-offset-2 outline-accent/40 outline-dashed',
                    hovered && 'ring-2 ring-accent',
                    dragging && day.locked && 'opacity-50',
                  )}
                  onDragOver={(ev) => {
                    if (!target) return;
                    ev.preventDefault();
                    const copy = drag?.kind === 'task' || ev.ctrlKey || ev.altKey || ev.metaKey;
                    ev.dataTransfer.dropEffect = copy ? 'copy' : 'move';
                    if (dropDay !== day.date) setDropDay(day.date);
                    if (dropCopy !== copy) setDropCopy(copy);
                  }}
                  onDragLeave={(ev) => {
                    if (ev.currentTarget.contains(ev.relatedTarget as Node | null)) return;
                    setDropDay((x) => (x === day.date ? null : x));
                  }}
                  onDrop={(ev) => {
                    ev.preventDefault();
                    const current = drag;
                    endDrag();
                    if (!current || day.locked) return;
                    if (current.kind === 'entry') {
                      const e = data.entries.find((x) => x.id === current.id);
                      if (e) moveOrCopy(e, day.date, ev.ctrlKey || ev.altKey || ev.metaKey);
                    } else {
                      // a task from the side panel: a new booking on this day; the task stays in its list
                      setDialog({ open: true, date: day.date, task: toPicked(current.task) });
                    }
                  }}
                >
                  <header
                    className={clsx(
                      'flex flex-col gap-1.5 rounded-t-lg border-b border-line px-3 py-2',
                      (day.holidayName || day.absence) && 'hatch',
                      state === 'full' && 'bg-good-soft',
                      state === 'over' && 'bg-warn-soft',
                    )}
                  >
                    <div className={clsx('flex gap-x-2', showWeekend ? 'flex-col' : 'flex-wrap items-baseline justify-between')}>
                      <span className="text-sm font-semibold whitespace-nowrap">
                        {wd(day.date)} <span className="num font-normal text-graphite">{d(day.date).slice(0, 6)}</span>
                      </span>
                      <span className="num inline-flex items-center gap-1 text-xs whitespace-nowrap text-graphite" title={state === 'full' ? t('week.dayFull') : state === 'over' ? t('week.dayOver') : undefined}>
                        {state === 'full' && <CheckCircle2 className="size-3.5 text-good" aria-label={t('week.dayFull')} />}
                        <span className={clsx('font-semibold', state === 'over' ? 'text-warn' : state === 'full' ? 'text-good' : 'text-ink')}>{h(day.bookedMinutes)}</span>
                        {cap !== null && <> / {h(cap)}</>}
                      </span>
                    </div>
                    {cap !== null && <Gauge value={day.bookedMinutes} capacity={cap} base={day.baseMinutes ?? undefined} comp={day.compensationMinutes ?? 0} />}
                    {(day.holidayName || day.absence || day.absencePending || day.locked || freeDay) && (
                      <div className="flex flex-wrap gap-1">
                        {freeDay && <Pill tone="neutral">{t('week.freeDay')}</Pill>}
                        {day.holidayName && <Pill tone="info">{day.holidayName}</Pill>}
                        {day.absence && <Pill tone="neutral">{t(`week.absence.${day.absence as 'vacation'}`)}</Pill>}
                        {day.absencePending && !day.absence && (
                          <Pill tone="warn">{t('week.absence.pending', { kind: t(`week.absence.${day.absencePending as 'vacation'}`) })}</Pill>
                        )}
                        {day.locked && (
                          <Pill tone="neutral">
                            <Lock className="size-3" />
                            {t('week.locked')}
                          </Pill>
                        )}
                      </div>
                    )}
                  </header>
                  <ul className="flex flex-1 flex-col">
                    {entries.length === 0 && <li className="px-3 py-3 text-xs text-faint">{t('week.noEntries')}</li>}
                    {entries.map((e) => {
                      const color = projectColorVar(e.projectColor);
                      const movable = own && e.perm.edit && !e.deletedAt;
                      return (
                        <li
                          key={e.id}
                          className={clsx(
                            'group flex flex-col gap-1 border-b border-line/70 py-2 pr-3 pl-3.5',
                            e.status === 'rejected' && 'bg-bad-soft/60',
                            movable && 'cursor-grab active:cursor-grabbing',
                            drag?.kind === 'entry' && drag.id === e.id && 'opacity-40',
                          )}
                          // the colour of the project as a thin bar on the left (the project number is written below)
                          style={color ? { boxShadow: `inset 3px 0 0 ${color}` } : undefined}
                          draggable={movable}
                          onDragStart={(ev) => {
                            ev.dataTransfer.setData('text/x-pz-entry', e.id);
                            ev.dataTransfer.effectAllowed = 'copyMove';
                            setDragPicture(ev, `${e.taskRef} ${e.taskTitle} · ${h(e.minutes)} h`);
                            setDrag({ kind: 'entry', id: e.id });
                          }}
                          onDragEnd={endDrag}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <button
                              type="button"
                              disabled={!e.perm.edit}
                              onClick={() => setDialog({ open: true, entry: e })}
                              className="min-w-0 text-left text-[13px] leading-snug hyphens-auto [overflow-wrap:anywhere] disabled:cursor-default"
                              title={`${e.projectNumber} ${e.projectName}`}
                            >
                              <span className="num font-semibold text-accent">{e.taskRef}</span> <span className="font-medium">{e.taskTitle}</span>
                              <span className="block truncate text-xs text-graphite">
                                {e.projectNumber} {e.projectName}
                              </span>
                            </button>
                            <span className="num text-sm font-semibold">{h(e.minutes)}</span>
                          </div>
                          {/* the whole description, with its paragraphs (as in "Meine Zeiten") */}
                          {e.description && <p className="text-xs whitespace-pre-line text-graphite hyphens-auto [overflow-wrap:anywhere]">{e.description}</p>}
                          {e.status === 'rejected' && e.rejectReason && <p className="text-xs text-bad">{e.rejectReason}</p>}
                          <div className="flex items-center justify-between gap-1">
                            <span className="flex flex-wrap gap-1">
                              <StatusPill status={e.status} />
                              <TaskBadges formaState={e.taskFormaState} internal={e.projectInternal} deleted={e.taskDeleted} />
                            </span>
                            <span className="opacity-100 transition-opacity xl:opacity-0 xl:group-hover:opacity-100 xl:group-focus-within:opacity-100">
                              {movable && (
                                <IconButton label={t('week.moveOrCopy')} icon={<ArrowRightLeft className="size-3.5" />} onClick={() => (setMoveFor(e), setMoveDate(e.workDate))} />
                              )}
                              <EntryActions entry={e} compact onEdit={(x) => setDialog({ open: true, entry: x })} />
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {!day.locked && (
                    <button
                      type="button"
                      onClick={() => setDialog({ open: true, date: day.date })}
                      className="flex items-center gap-1 rounded-b-lg px-3 py-2 text-left text-xs font-medium text-accent hover:bg-accent-soft"
                    >
                      <Plus className="size-3.5" /> {t('week.addEntry')}
                    </button>
                  )}
                  {hovered && (
                    <div className="pointer-events-none absolute inset-x-2 bottom-2 flex justify-center" aria-hidden>
                      <span className="rounded-md bg-accent px-2.5 py-1 text-xs font-semibold text-accent-ink shadow">
                        {drag?.kind === 'task' ? t('week.dropNew', { day: `${wd(day.date)} ${d(day.date).slice(0, 6)}` }) : dropCopy ? t('week.dropCopy') : t('week.dropMove')}
                      </span>
                    </div>
                  )}
                </section>
              );
            })}
          </div>

          {own && (
            <aside className="w-full shrink-0 2xl:w-80">
              <div className="rounded-lg border border-line bg-sheet">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
                  <h2 className="text-sm font-semibold">{t('quick.favorites')}</h2>
                  <Select aria-label={t('quick.sortLabel')} className="w-44" value={sort} onChange={(e) => setSort(e.target.value as PanelSort)}>
                    <option value="manual">{t('quick.sort.manual')}</option>
                    <option value="hours">{t('quick.sort.hours')}</option>
                    <option value="project">{t('quick.sort.project')}</option>
                  </Select>
                </div>
                <TaskList
                  tasks={favTasks}
                  grouped={sort === 'project'}
                  empty={t('quick.favoritesEmpty')}
                  renderRow={(r, i) => (
                    <QuickTaskRow
                      key={r.id}
                      task={r}
                      favs={favs}
                      hours={usage[r.id]}
                      dragging={drag?.kind === 'task' && drag.task.id === r.id}
                      onPick={(x) => setDialog({ open: true, date: bookDate, task: x })}
                      onDragStart={(ev) => {
                        ev.dataTransfer.setData('text/x-pz-task', r.id);
                        ev.dataTransfer.effectAllowed = 'copyMove';
                        setDragPicture(ev, `${r.ref} ${r.title}`);
                        setDrag({ kind: 'task', task: r, from: 'fav' });
                      }}
                      onDragEnd={endDrag}
                      // own order: drop between the favorites; the up/down buttons do the same without a mouse
                      reorder={
                        sort === 'manual'
                          ? {
                              index: i,
                              count: favTasks.length,
                              dropBefore: favDrop === i,
                              dropAfter: favDrop === favTasks.length && i === favTasks.length - 1,
                              onOver: (after) => drag?.kind === 'task' && setFavDrop(after ? i + 1 : i),
                              onDrop: () => {
                                const at = favDrop ?? i;
                                endDrag();
                                dropOnFavorites(at);
                              },
                              move: (delta) => reorder(r.id, i + delta),
                            }
                          : undefined
                      }
                    />
                  )}
                  onDropEmpty={
                    // the list is empty or sorted: a task from "zuletzt verwendet" dropped on it is pinned at the top
                    drag?.kind === 'task' && drag.from === 'recent'
                      ? () => {
                          endDrag();
                          dropOnFavorites(0);
                        }
                      : undefined
                  }
                />
                {recentOnly.length > 0 && (
                  <>
                    <h2 className="border-y border-line px-3 py-2 text-sm font-semibold">{t('week.recent')}</h2>
                    <TaskList
                      tasks={recentOnly}
                      grouped={sort === 'project'}
                      empty=""
                      renderRow={(r) => (
                        <QuickTaskRow
                          key={r.id}
                          task={r}
                          favs={favs}
                          hours={usage[r.id]}
                          dragging={drag?.kind === 'task' && drag.task.id === r.id}
                          onPick={(x) => setDialog({ open: true, date: bookDate, task: x })}
                          onDragStart={(ev) => {
                            ev.dataTransfer.setData('text/x-pz-task', r.id);
                            ev.dataTransfer.effectAllowed = 'copyMove';
                            setDragPicture(ev, `${r.ref} ${r.title}`);
                            setDrag({ kind: 'task', task: r, from: 'recent' });
                          }}
                          onDragEnd={endDrag}
                        />
                      )}
                    />
                  </>
                )}
                {recentOnly.length === 0 && favs.tasks.length === 0 && <p className="border-t border-line px-3 py-3 text-xs text-graphite">{t('week.recentEmpty')}</p>}
                <p className="px-3 py-2 text-xs text-faint">{t('week.tipDrag')}</p>
              </div>

              {/* recurring entries of the person (request 3) */}
              <div className="mt-3 rounded-lg border border-line bg-sheet">
                <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
                  <h2 className="flex items-center gap-1.5 text-sm font-semibold">
                    <Repeat className="size-4 text-graphite" aria-hidden />
                    {t('recurring.title')}
                  </h2>
                  <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => setDialog({ open: true, date: bookDate })}>
                    {t('recurring.add')}
                  </Button>
                </div>
                {ruleList.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-graphite">{t('recurring.empty')}</p>
                ) : (
                  <ul>
                    {ruleList.map((r) => (
                      <li key={r.id} className={clsx('flex items-start gap-2 border-b border-line/60 px-3 py-2 last:border-b-0', !r.active && 'text-graphite')}>
                        <div className="min-w-0 flex-1 text-[13px] leading-snug">
                          <span className="num font-semibold text-accent">{r.task?.ref ?? ''}</span> {r.task?.title ?? t('quick.unknownTask')}
                          <span className="block text-xs text-graphite">
                            <ProjectDot color={r.task?.projectColor} className="mr-1" />
                            {r.weekdays.map((n) => wd(addDays(WEEK_REFERENCE, n - 1))).join(', ')} · <span className="num">{h(r.minutes)} h</span>
                            {r.endsOn ? ` · ${t('recurring.untilShort', { date: d(r.endsOn) })}` : ''}
                          </span>
                          {!r.active && <Pill className="mt-1">{t('recurring.paused')}</Pill>}
                          {r.active && !r.bookable && (
                            <Pill tone="warn" className="mt-1">
                              {t('recurring.notBookable')}
                            </Pill>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center">
                          <IconButton label={t('recurring.edit')} icon={<Pencil className="size-3.5" />} onClick={() => setDialog({ open: true, rule: r })} />
                          <IconButton
                            label={r.active ? t('recurring.pause') : t('recurring.resume')}
                            icon={r.active ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
                            onClick={() =>
                              saveRuleM
                                .mutateAsync({
                                  id: r.id,
                                  taskId: r.taskId,
                                  weekdays: r.weekdays,
                                  minutes: r.minutes,
                                  description: r.description,
                                  billingType: r.billingType,
                                  workLocation: r.workLocation,
                                  startsOn: r.startsOn,
                                  endsOn: r.endsOn,
                                  active: !r.active,
                                })
                                .then(() => toast.ok(r.active ? t('recurring.pausedDone') : t('recurring.resumedDone')))
                                .catch(toast.error)
                            }
                          />
                          <IconButton label={t('recurring.delete')} icon={<Trash2 className="size-3.5" />} onClick={() => setRuleToDelete(r)} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </aside>
          )}
        </div>
      )}

      <EntryDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false })}
        entry={dialog.entry}
        rule={dialog.rule}
        defaultDate={dialog.date}
        defaultTask={dialog.task}
        personId={personId}
        side
      />
      <LeaveDialog open={!!absence} mode="any" date={absence} onClose={() => setAbsence(null)} />
      {own && <CopyWeekDialog open={copyOpen} onClose={() => setCopyOpen(false)} targetWeek={ws} shownEntries={data?.entries ?? []} />}
      <ConfirmModal
        open={!!ruleToDelete}
        onClose={() => setRuleToDelete(null)}
        title={t('recurring.deleteTitle')}
        text={t('recurring.deleteText')}
        confirmLabel={t('recurring.delete')}
        danger
        loading={deleteRuleM.isPending}
        error={deleteRuleM.error}
        onConfirm={() =>
          ruleToDelete &&
          deleteRuleM
            .mutateAsync({ id: ruleToDelete.id })
            .then(() => {
              setRuleToDelete(null);
              toast.ok(t('recurring.deleted'));
            })
            .catch(() => {})
        }
      />
      <Modal
        open={!!moveFor}
        onClose={() => setMoveFor(null)}
        title={t('week.moveOrCopy')}
        footer={
          <>
            <Button onClick={() => setMoveFor(null)}>{t('common.cancel')}</Button>
            <Button disabled={!moveDate || !moveFor} loading={copyM.isPending} onClick={() => moveFor && moveOrCopy(moveFor, moveDate, true).then(() => setMoveFor(null))}>
              {t('week.copyHere')}
            </Button>
            <Button
              variant="primary"
              disabled={!moveDate || !moveFor || moveDate === moveFor.workDate}
              loading={updateM.isPending}
              onClick={() => moveFor && moveOrCopy(moveFor, moveDate, false).then(() => setMoveFor(null))}
            >
              {t('week.moveHere')}
            </Button>
          </>
        }
      >
        {moveFor && (
          <div className="flex flex-col gap-3">
            <p className="text-sm">
              <span className="num font-semibold text-accent">{moveFor.taskRef}</span> {moveFor.taskTitle} · {d(moveFor.workDate)} · {h(moveFor.minutes)} h
            </p>
            <Field label={t('week.targetDate')}>
              <Input type="date" className="w-48" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} />
            </Field>
            <p className="text-xs text-graphite">{t('week.dragHint')}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

/** A list of the side panel; grouped by project with a heading per project when sorted that way. */
function TaskList({
  tasks,
  grouped,
  empty,
  renderRow,
  onDropEmpty,
}: {
  tasks: TaskView[];
  grouped: boolean;
  empty: string;
  renderRow: (t: TaskView, index: number) => ReactNode;
  onDropEmpty?: () => void;
}) {
  const dropProps = onDropEmpty
    ? {
        onDragOver: (ev: DragEvent) => ev.preventDefault(),
        onDrop: (ev: DragEvent) => {
          ev.preventDefault();
          onDropEmpty();
        },
      }
    : {};
  if (!tasks.length) {
    return empty ? (
      <p className={clsx('px-3 py-3 text-xs text-graphite', onDropEmpty && 'm-2 rounded-md border border-dashed border-accent text-accent')} {...dropProps}>
        {empty}
      </p>
    ) : null;
  }
  if (!grouped)
    return (
      <ul className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-1" {...dropProps}>
        {tasks.map((x, i) => renderRow(x, i))}
      </ul>
    );
  const groups: { key: string; head: TaskView; items: { task: TaskView; index: number }[] }[] = [];
  tasks.forEach((task, index) => {
    const g = groups.find((x) => x.key === task.projectId);
    if (g) g.items.push({ task, index });
    else groups.push({ key: task.projectId, head: task, items: [{ task, index }] });
  });
  return (
    <div {...dropProps}>
      {groups.map((g) => (
        <div key={g.key}>
          <p className="flex items-center gap-1.5 truncate bg-sheet-2 px-3 py-1 text-xs font-medium text-graphite">
            <ProjectDot color={g.head.projectColor} />
            {g.head.projectNumber} {g.head.projectName}
          </p>
          <ul className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-1">{g.items.map((x) => renderRow(x.task, x.index))}</ul>
        </div>
      ))}
    </div>
  );
}

/**
 * One task of the side panel: a click starts a booking with this task, dragging it onto a day books it there (the task
 * stays in the list), the star pins or unpins it. In the own order the favorites take a dragged task between them and
 * move with the arrow buttons.
 */
function QuickTaskRow({
  task,
  favs,
  hours,
  dragging,
  onPick,
  onDragStart,
  onDragEnd,
  reorder,
}: {
  task: TaskView;
  favs: ReturnType<typeof useFavorites>;
  hours?: number;
  dragging?: boolean;
  onPick: (t: PickedTask) => void;
  onDragStart: (ev: DragEvent) => void;
  onDragEnd: () => void;
  reorder?: {
    index: number;
    count: number;
    dropBefore: boolean;
    dropAfter: boolean;
    onOver: (after: boolean) => void;
    onDrop: () => void;
    move: (delta: number) => void;
  };
}) {
  const { t } = useTranslation();
  return (
    <li
      className={clsx(
        'group/row relative flex items-stretch border-b border-line/60 hover:bg-sheet-2',
        dragging && 'opacity-40',
        reorder?.dropBefore && 'before:absolute before:inset-x-2 before:-top-px before:h-0.5 before:rounded before:bg-accent',
        reorder?.dropAfter && 'after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded after:bg-accent',
      )}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={
        reorder
          ? (ev) => {
              ev.preventDefault();
              const r = ev.currentTarget.getBoundingClientRect();
              reorder.onOver(ev.clientY > r.top + r.height / 2);
            }
          : undefined
      }
      onDrop={
        reorder
          ? (ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              reorder.onDrop();
            }
          : undefined
      }
    >
      <span className="flex cursor-grab items-center pl-1.5 text-faint active:cursor-grabbing" aria-hidden>
        <GripVertical className="size-3.5" />
      </span>
      <button type="button" className="flex min-w-0 flex-1 items-start gap-2 py-2 pr-1 pl-1 text-left" onClick={() => onPick(toPicked(task))}>
        <span className="min-w-0 text-[13px] leading-snug">
          <span className="num font-semibold text-accent">{task.ref}</span> {task.title}
          <span className="flex items-center gap-1 truncate text-xs text-graphite">
            <ProjectDot color={task.projectColor} />
            <span className="truncate">
              {task.projectNumber} {task.projectName}
            </span>
            {!!hours && <span className="num ml-auto shrink-0 pl-1 text-faint">{h(hours, 0)} h</span>}
          </span>
        </span>
      </button>
      {reorder && (
        <span className="flex flex-col justify-center opacity-100 xl:opacity-0 xl:group-hover/row:opacity-100 xl:group-focus-within/row:opacity-100">
          <IconButton label={t('quick.moveUp')} icon={<ArrowUp className="size-3" />} disabled={reorder.index === 0} onClick={() => reorder.move(-1)} />
          <IconButton label={t('quick.moveDown')} icon={<ArrowDown className="size-3" />} disabled={reorder.index >= reorder.count - 1} onClick={() => reorder.move(1)} />
        </span>
      )}
      <FavoriteStar className="mt-1 self-start" on={favs.ids.has(task.id)} disabled={favs.pending} onToggle={() => favs.toggle(task.id)} />
    </li>
  );
}
